package com.exura.pages.planCreator.components.seriesViews

import androidx.compose.runtime.*
import com.exura.features.plandefinition.domain.builder.series.SimpleInstructionSeriesBuilderNew
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.domain.model.series.HourOfDay
import com.exura.features.plandefinition.domain.model.series.PartOfDay
import com.exura.features.plandefinition.domain.model.series.SeriesType
import com.exura.pages.planCreator.components.ViewMode
import com.exura.pages.planCreator.components.seriesViews.inputs.*
import com.exura.pages.planCreator.components.seriesViews.sections.*
import com.exura.pages.planCreator.model.SeriesTypeRegistry
import com.exura.ui.Dimens
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import org.jetbrains.compose.web.css.px
import kotlin.math.abs

/**
 * Enhanced Simple Instruction Series editor view that works with SimpleInstructionSeriesBuilderNew
 * Provides real-time editing with change tracking and full progressive disclosure flow
 */
@Composable
internal fun SimpleInstructionSeriesView(
    builder: SimpleInstructionSeriesBuilderNew,
    isNewSeries: Boolean = true,
    viewMode: ViewMode,
    planType: PlanType,
    onAction: () -> Unit,
) {
    val typeInfo = SeriesTypeRegistry.getTypeInfo(SeriesType.SIMPLE_INSTRUCTION)!!
    val inputMode = if (viewMode == ViewMode.EDIT) PlanCreatorInputMode.EDIT else PlanCreatorInputMode.VIEW

    var name by remember(builder) { mutableStateOf("") }
    var description by remember(builder) { mutableStateOf("") }
    var estimatedMinutes by remember(builder) { mutableStateOf(5) }
    var instruction by remember(builder) { mutableStateOf("") }
    var relativeDay by remember(builder) { mutableStateOf(0) }
    var spawnEveryDays by remember(builder) { mutableStateOf( 1) }
    var spawnCount by remember(builder) { mutableStateOf<Int?>(1) }
    var spawnForDays by remember(builder) { mutableStateOf<Int?>(null) }
    var partsOfDay by remember(builder) { mutableStateOf(listOf<PartOfDay>()) }
    var exactTimes by remember(builder) { mutableStateOf(listOf<HourOfDay>()) }

    var relativeDayMode by remember(builder) { mutableStateOf(RelativeDayMode.ON_VISIT_DAY) }
    var spawnFrequencyMode by remember(builder) { mutableStateOf(FrequencyMode.SINGLE) }
    var spawnMode by remember(builder) { mutableStateOf(SpawnMode.TIMES) }
    var timeSelectionMode by remember(builder) { mutableStateOf(TimeSelectionMode.PARTS_OF_DAY) }

    var hasChanges by remember(builder) { mutableStateOf(false) }
    var isValid by remember(builder) { mutableStateOf(false) }

    var relativeDayDisplay by remember(builder) { mutableStateOf(relativeDay.coerceAtLeast(1)) }
    var spawnEveryDaysDisplay by remember(builder) { mutableStateOf( spawnEveryDays.coerceAtLeast(2)) }
    var spawnCountDisplay by remember(builder) { mutableStateOf(spawnCount?.coerceAtLeast(2) ?: 2) }
    var spawnForDaysDisplay by remember(builder) { mutableStateOf(spawnForDays?.coerceAtLeast(2) ?: 2) }

    val seriesHasName by remember(name) { mutableStateOf(name.isNotBlank()) }
    val hasMainElement by remember(instruction) { mutableStateOf(instruction.isNotBlank()) }
    val isSingleMode by remember(spawnFrequencyMode) { mutableStateOf(spawnFrequencyMode == FrequencyMode.SINGLE) }

    fun updateChanges() {
        hasChanges = builder.isChanged
        val m = builder.validate()
        isValid = m.isEmpty()
    }

    LaunchedEffect(builder) {
        val builderSpawnCount = builder.spawnCount()
        val builderSpawnForDays = builder.spawnForDays()

        name = builder.name() ?: name
        description = builder.description() ?: description
        estimatedMinutes = builder.estimatedMinutes() ?: estimatedMinutes
        instruction = builder.instruction() ?: instruction
        relativeDay = builder.relativeDay() ?: relativeDay
        spawnEveryDays = builder.spawnEveryDays() ?: spawnEveryDays
        partsOfDay = builder.partsOfDay() ?: partsOfDay
        exactTimes = builder.exactTimes() ?: exactTimes

        if (builderSpawnCount != null) {
            spawnCount = builderSpawnCount
            spawnForDays = null
        } else if (builderSpawnForDays != null) {
            spawnCount = null
            spawnForDays = builderSpawnForDays
        } else {
            spawnCount = 1
            spawnForDays = null
        }

        relativeDayMode = if (relativeDay == 0) RelativeDayMode.ON_VISIT_DAY else RelativeDayMode.DAYS_OFFSET
        spawnFrequencyMode = when {
            spawnCount == 1 -> FrequencyMode.SINGLE
            spawnEveryDays == 1 -> FrequencyMode.DAILY
            else -> FrequencyMode.EVERY_X_DAYS
        }
        spawnMode = if (spawnForDays != null) SpawnMode.FOR_DAYS else SpawnMode.TIMES
        timeSelectionMode = if (exactTimes.isEmpty()) TimeSelectionMode.PARTS_OF_DAY else TimeSelectionMode.SPECIFIC_TIMES

        builder.setName(name)
        builder.setDescription(description)
        builder.setEstimatedMinutes(estimatedMinutes)
        builder.setInstruction(instruction)
        builder.setRelativeDay(relativeDay)
        builder.setSpawnEveryDays(spawnEveryDays)
        if(partsOfDay.isNotEmpty()) {
            builder.setPartsOfDay(partsOfDay)
        } else if(exactTimes.isNotEmpty()) {
            builder.setExactTimes(exactTimes)
        }
        builder.setSpawnCount(spawnCount)
        builder.setSpawnForDays(spawnForDays)
        relativeDayDisplay = when (planType) {
            PlanType.PREPLAN -> abs(relativeDay).coerceAtLeast(1)
            PlanType.POSTPLAN -> relativeDay.coerceAtLeast(1)
        }
        spawnEveryDaysDisplay = spawnEveryDays.coerceAtLeast(2)
        spawnCountDisplay = spawnCount?.coerceAtLeast(2) ?: 2
        spawnForDaysDisplay = spawnForDays?.coerceAtLeast(2) ?: 2
    }

    SeriesView(
        hasChanges = hasChanges,
        isValid = isValid,
        typeInfo = typeInfo,
        isNewSeries = isNewSeries,
        viewMode = inputMode,
        onAction = {
            onAction()
            builder.markSaved()
            updateChanges()
        },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(32.px)
                .overflow(Overflow.Auto),
            verticalArrangement = Arrangement.spacedBy(0.px)
        ) {

            NameSeriesSection(
                value = name,
                onValueChange = { new ->
                    name = new
                    builder.setName(new)
                    updateChanges()
                },
                mode = inputMode,
            )

            DescriptionSeriesSection(
                value = description,
                onValueChange = { new ->
                    description = new
                    builder.setDescription(new)
                    updateChanges()
                },
                mode = inputMode,
                placeholder = "Komentarz eksperta. Dlaczego pacjent miałby wykonać to zadanie? Jak to zrobić? Czy potrzebuje dodatkowych materiałów?",
                enabled = seriesHasName,
            )

            EstimatedMinutesSection(
                estimatedMinutes = estimatedMinutes,
                onEstimatedMinutesChange = { new ->
                    estimatedMinutes = new
                    builder.setEstimatedMinutes(new)
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName,
            )

            ElementMainNameSection(
                value = instruction,
                onValueChange = { new ->
                    instruction = new
                    builder.setInstruction(new)
                    updateChanges()
                },
                mode = inputMode,
                title = "Instrukcja",
                placeholder = "np. Wykonaj ćwiczenia oddechowe...",
                enabled = seriesHasName,
            )

            SpawnEveryDaysSection(
                selectedValue = spawnFrequencyMode,
                spawnEveryDays = spawnEveryDaysDisplay,
                onModeChange = { newMode ->
                    spawnFrequencyMode = newMode
                    when (newMode) {
                        FrequencyMode.SINGLE -> {
                            builder.setSpawnEveryDays(1)
                            builder.setSpawnCount(1)
                            builder.setSpawnForDays(null)
                            spawnEveryDays = 1
                            spawnCount = 1
                            spawnForDays = null
                        }

                        FrequencyMode.DAILY -> {
                            builder.setSpawnEveryDays(1)
                            spawnForDays = 1
                        }

                        FrequencyMode.EVERY_X_DAYS -> {
                            builder.setSpawnEveryDays(spawnEveryDaysDisplay)
                            spawnForDays = spawnForDaysDisplay
                        }
                    }
                    updateChanges()
                },
                onSpawnEveryDaysChange = { new ->
                    spawnFrequencyMode = FrequencyMode.EVERY_X_DAYS
                    spawnEveryDays = new
                    builder.setSpawnEveryDays(new)

                    updateChanges()
                },
                mode = inputMode,
                enabled = instruction.isNotBlank(),
            )

            RelativeDaySection(
                selectedValue = relativeDayMode,
                relativeDay = relativeDayDisplay,
                onRelativeDayChange = { new ->
                    relativeDayMode = RelativeDayMode.DAYS_OFFSET
                    relativeDayDisplay = new
                    relativeDay = when (planType) {
                        PlanType.PREPLAN -> -new
                        PlanType.POSTPLAN -> new
                    }
                    builder.setRelativeDay(relativeDay)
                    updateChanges()
                },
                onModeChange = { newMode ->
                    relativeDayMode = newMode
                    when(newMode) {
                        RelativeDayMode.NONE -> {}
                        RelativeDayMode.ON_VISIT_DAY -> {
                            builder.setRelativeDay(0)
                            relativeDay = 0
                        }
                        RelativeDayMode.DAYS_OFFSET -> {
                            relativeDay = when (planType) {
                                PlanType.PREPLAN -> -relativeDayDisplay
                                PlanType.POSTPLAN -> relativeDayDisplay
                            }
                            builder.setRelativeDay(relativeDay)
                        }
                    }
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName && hasMainElement,
                planType = planType,
            )

            SpawnModeSection(
                selectedValue = spawnMode,
                spawnCount = spawnCountDisplay,
                spawnForDays = spawnForDaysDisplay,
                onModeChange = { newMode ->
                    spawnMode = newMode
                    when (newMode) {
                        SpawnMode.TIMES -> {
                            builder.setSpawnCount(spawnCountDisplay)
                            builder.setSpawnForDays(null)
                            spawnCount = spawnCountDisplay
                            spawnForDays = null
                        }
                        SpawnMode.FOR_DAYS -> {
                            builder.setSpawnCount(null)
                            builder.setSpawnForDays(spawnForDaysDisplay)
                            spawnCount = null
                            spawnForDays = spawnForDaysDisplay
                        }
                    }
                    updateChanges()
                },
                onSpawnCountChange = { new ->
                    spawnCount = new
                    spawnCountDisplay = new
                    builder.setSpawnCount(new)
                    builder.setSpawnForDays(null)
                    updateChanges()
                },
                onSpawnForDaysChange = { new ->
                    spawnForDays = new
                    spawnForDaysDisplay = new
                    builder.setSpawnForDays(new)
                    builder.setSpawnCount(null)
                    updateChanges()
                },
                mode = inputMode,
                enabled = seriesHasName && hasMainElement && !isSingleMode,
            )

            TimeSelectionSection(
                selectedMode = timeSelectionMode,
                onModeChange = { newMode ->
                    timeSelectionMode = newMode
                    when (newMode) {
                        TimeSelectionMode.PARTS_OF_DAY -> {
                            builder.setPartsOfDay(partsOfDay)
                            builder.setExactTimes(null)
                        }

                        TimeSelectionMode.SPECIFIC_TIMES -> {
                            builder.setPartsOfDay(null)
                            builder.setExactTimes(exactTimes)
                        }
                    }
                    updateChanges()
                },
                mode = inputMode,
                enabled = instruction.isNotBlank(),
                selectedPartsOfDay = partsOfDay,
                onPartsOfDayChange = {
                    partsOfDay = it
                    builder.setPartsOfDay(it)
                    builder.setExactTimes(null)
                    updateChanges()
                },
                selectedSpecificTimes = exactTimes,
                onSpecificTimesChange = {
                    exactTimes = it
                    builder.setPartsOfDay(null)
                    builder.setExactTimes(it)
                    updateChanges()
                }
            )

        }
    }
}
